/**
 * User role utilities.
 * Frontend uses these to decide dashboard priority and context.
 */

import { ROLE_PRIORITY_ORDER, type UserRole } from '../constants/roles';
import type { UserRoleWithAcademy } from '../types';

/**
 * Pick the highest-priority role from roles. Frontend can use this for default
 * dashboard routing; override as needed for context switching.
 */
export function pickPrimaryRole(
  roles: UserRoleWithAcademy[] | string[]
): UserRole {
  const roleStrings = Array.isArray(roles) && roles.length > 0
    ? (typeof roles[0] === 'string' ? roles as string[] : (roles as UserRoleWithAcademy[]).map((r) => r.role))
    : [];
  for (const r of ROLE_PRIORITY_ORDER) {
    if (roleStrings.includes(r)) return r;
  }
  return 'guardian';
}

/**
 * Derive unique role names from rolesWithAcademy.
 */
export function getUniqueRoles(rolesWithAcademy: UserRoleWithAcademy[]): UserRole[] {
  return [...new Set(rolesWithAcademy.map((r) => r.role))];
}

/**
 * Merge flat `roles` and academy-scoped roles for the client user store.
 * Unions both sources so platform-wide roles (e.g. admin) are not dropped when
 * rolesWithAcademy is present, and new academy roles are not missed when flat
 * roles is stale.
 */
export function mergeClientRoles(
  flatRoles: string[] | undefined | null,
  rolesWithAcademy: UserRoleWithAcademy[] | undefined | null
): UserRole[] {
  const fromAcademy = rolesWithAcademy?.length ? getUniqueRoles(rolesWithAcademy) : [];
  const fromFlat = flatRoles?.length ? (flatRoles as UserRole[]) : [];
  const merged = [...new Set([...fromAcademy, ...fromFlat])];
  return merged.length > 0 ? merged : ['guardian'];
}

/**
 * Minimal user shape accepted by role-checking utilities.
 * Compatible with the full User type from @shared/types.
 */
export interface RoleCheckable {
  roles?: string[];
  rolesWithAcademy?: UserRoleWithAcademy[];
  hasAcademyProfile?: boolean;
}

/**
 * Get the primary (highest-priority) role for a user.
 * Use when you need a single role for display or routing.
 */
export function getPrimaryRole(user: RoleCheckable | null | undefined): UserRole {
  if (!user) return 'guardian';
  if (user.rolesWithAcademy?.length) {
    return pickPrimaryRole(user.rolesWithAcademy);
  }
  if (user.roles?.length) {
    return pickPrimaryRole(user.roles);
  }
  return 'guardian';
}

/**
 * Check whether a user has a specific role.
 *
 * Checks `user.roles` (or derives from rolesWithAcademy). For `guardian` and
 * `student` roles, also accepts users who have an academy profile but whose
 * role row hasn't been written yet (role is set on first booking).
 */
export function hasRole(
  user: RoleCheckable | null | undefined,
  role: UserRole
): boolean {
  if (!user) return false;
  const userRoles = mergeClientRoles(user.roles, user.rolesWithAcademy);
  if (userRoles.includes(role)) return true;
  if ((role === 'guardian' || role === 'student') && user.hasAcademyProfile) return true;
  return false;
}

/**
 * Check whether a user has at least one of the given roles.
 */
export function hasAnyRole(
  user: RoleCheckable | null | undefined,
  roles: UserRole[]
): boolean {
  return roles.some((r) => hasRole(user, r));
}

/**
 * Academy owner or delegated academy admin — same management dashboard scope
 * (use owner-only checks for payments, Stripe Connect, deleting the academy, etc.).
 */
export function hasAcademyManagementRole(user: RoleCheckable | null | undefined): boolean {
  return hasRole(user, 'academy_owner') || hasRole(user, 'academy_admin');
}

/**
 * Club owner or delegated club admin — same management dashboard scope
 * (use owner-only checks for club payments, Connect, etc.).
 */
export function hasClubManagementRole(user: RoleCheckable | null | undefined): boolean {
  return hasRole(user, 'club_owner') || hasRole(user, 'club_admin');
}

/**
 * Any organization management role (academy and/or club operator surfaces).
 */
export function hasOrganizationManagementRole(user: RoleCheckable | null | undefined): boolean {
  return hasAcademyManagementRole(user) || hasClubManagementRole(user);
}
